import { createSelector } from 'reselect';
import { ServiceRequestModel } from '@/typings/models/ServiceRequest';

const selectServiceRequest = (state: DefaultRootState) => state.serviceRequest;

export const selectServiceRequestDrawer = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.createDrawer
);

export const selectServiceRequestDetailsDrawer = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.detailsDrawer
);

export const selectCalendarEventDetailsDrawer = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.calendarEventDetailsDrawer
);

export const selectHighlightedServiceRequest = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.highlightedServiceRequest
);

export const selectFetchCalendarViewAt = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.fetchCalendarViewAt
);

export const selectCalendarEvents = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.calendarEvents
);

export const selectCalendarUnavailabilities = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.calendarUnavailabilities
);

// Session state on purpose: the calendar always opens on today, so the viewed
// day is never persisted (see serviceRequest/setCalendarDate).
// Keyed on the raw value, not the slice: consumers re-fetch on a new date
// identity, so an unrelated serviceRequest action must not mint a new Date.
export const selectCalendarDate = createSelector(
  (state: DefaultRootState) => state?.serviceRequest?.calendarDate,
  (date) => {
    if (!date) return new Date();
    return ['string', 'number'].includes(typeof date) ? new Date(date) : date;
  }
);

export const selectQueriedCalendarDate = createSelector(
  [selectServiceRequest],
  (serviceRequest) => {
    const date = serviceRequest?.queriedCalendarDate;
    return typeof date === 'string' ? new Date(date) : date;
  }
);

export const selectServiceRequestList = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.list || {}
);

export const selectServiceRequestDetails = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest?.details || {}
);

export const selectServiceRequestLoading = (actionType: string) => createSelector(
  [(state: DefaultRootState) => state.loading],
  (loading) => loading?.effects?.[actionType] || false
);

export const selectServiceRequestById = createSelector(
  [selectServiceRequestDetails, (_, id) => id],
  (details, id) : ServiceRequestModel => details[id]
);

export const selectCalendarEventById = createSelector(
  [selectServiceRequestDetails, (_, id) => id],
  (details, id) => details[id]
);
export const selectMultiBookingsList = createSelector(
  [selectServiceRequest],
  (serviceRequest) => serviceRequest.multiBookingsList || [],
);

export const selectCalendarEventsForUser = (userId: string | number | undefined) =>
  createSelector(
    [selectCalendarEvents],
    (events) => {
      if (!userId) return [];
      return events.filter((e: any) => String(e.assigneeId) === String(userId));
    },
  );

export const selectDateState = (state: DefaultRootState) => state.date;
